import { BillingComponent } from 'components/Billing';
import { EditPlan } from 'components/Billing/edit-plan';
import { ConfirmBoxContainer } from 'containers/ConfirmBox';
import GenTaxImage from 'assets/images/gen-tax-img.svg';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import routes from 'routes';
import {
  ContactUsMessageType,
  SubscriptionPlan,
  AccountType,
} from 'store/api/enums';
import {
  turnDrawerOff,
  turnDrawerOn,
  turnMessageOn,
  turnModalOn,
} from 'store/app/actions';
import { DrawerType, WindowType } from 'store/app/enums';
import { DrawerData, MessageData, ModalData } from 'store/app/types';
import { getAuthUser } from 'store/auth/selectors';
import { Plan } from 'store/billing/models';
import Features from 'store/billing/models/features.json';
import {
  getBillingLoading,
  getCurrentSubscription,
  getInvoices,
  getSubscriptionPlanTypes,
} from 'store/billing/selectors';
import { checkPageLoaded } from 'utils/check-page-loaded';
import { ReactComponent as BreakingHeartIcon } from 'assets/images/icons/breakingHeartIcon.svg';
import {
  cancelSubscriptionRequest,
  changeSubscriptionPlanRequest,
  fetchSubscriptionTypesRequest,
  getCurrentSubscriptionRequest,
  getInvoicesRequest,
  sendContactUsEmailRequest,
  updateCardRequest,
} from 'store/billing/actions';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageStates } from 'containers/MessageBox';
import { fetchMeRequest } from 'store/auth/actions';
import { Form, Input, Space } from 'antd';
import EmailLogo from 'assets/images/Emails-amico@3x.png';
import { CenterLogo } from 'components/Custom/logo';
import { BillingCheckout } from 'store/billing/models/billingCheckout';

const { TextArea } = Input;

export const BillingPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [contactUsForm] = Form.useForm();
  const [showPage, setShowPage] = React.useState(false);
  //const querpar = new URLSearchParams(location.search);

  //const cardUpdated = querpar.get('cardUpdated') === 'true';
  //const successfulPayment = querpar.get('successfulPayment') === 'true';
  const currentUser = useSelector(getAuthUser);
  const invoices = useSelector(getInvoices);
  const currentSubscription = useSelector(getCurrentSubscription);
  const isLoading = useSelector(getBillingLoading);
  const subscriptionPlanTypes = useSelector(getSubscriptionPlanTypes);

  // Check if user is owner, if not redirect to main page
  React.useEffect(() => {
    if (
      currentUser &&
      AccountType[currentUser.accountType] !== AccountType.OWNER
    ) {
      navigate(routes.main);
    }
  }, [currentUser]);

  React.useEffect(() => {
    setShowPage(true);
    currentUser && checkPageLoaded(location.pathname, routes.billing.root);
    if (currentUser?.accountUuid) {
      dispatch(getInvoicesRequest(currentUser?.accountUuid));
      if (!currentSubscription) {
        dispatch(getCurrentSubscriptionRequest(currentUser?.accountUuid));
      }
    }

    if (!subscriptionPlanTypes || subscriptionPlanTypes.length === 0) {
      dispatch(fetchSubscriptionTypesRequest());
    }
  }, []);

  const backToChangePlan = () => {
    navigate(routes.pricing.root);
  };

  const getFeaturesOfPlans = () => {
    const connectedOrganisationsFeature = Features.find(
      (fet) => fet.feature?.toLowerCase() === 'entities included (up to)'
    );

    const priceFeature = Features.find(
      (fet) => fet.feature?.toLowerCase() === 'price'
    );

    const plans = Object.values(SubscriptionPlan).filter(
      (plan) => plan != SubscriptionPlan.FREE_PLAN
    );

    return plans.map((planType) => {
      const planSize = planType.split(' ')[0]?.toLowerCase();
      const plan: Plan = {
        planName: planType,
        connectedOrganisations: connectedOrganisationsFeature
          ? connectedOrganisationsFeature[planSize]
          : undefined,
        price: priceFeature ? priceFeature[planSize] : undefined,
        vatNumberCheckerAddon: 2,
        additionalOrganisations: 2,
        totalAmount: priceFeature ? priceFeature[planSize] : undefined,
      };
      return plan;
    });
  };

  const updatePlan = (billing: BillingCheckout) => {
    dispatch(
      currentSubscription &&
        changeSubscriptionPlanRequest(currentSubscription?.typeId, billing)
    );
    dispatch(turnDrawerOff());
  };

  const updateCard = () => {
    currentUser && dispatch(updateCardRequest(currentUser?.accountUuid));
  };

  const closeDrawer = () => {
    dispatch(turnDrawerOff());
  };

  const downgradePlan = (typeId: string) => {
    const modalData: ModalData = {
      type: WindowType.WARNING,
      title: t('billing.downgrade-confirm-title'),
      icon: <img src={GenTaxImage} alt="" />,
      component: (
        <ConfirmBoxContainer
          callback={() => checkoutToUpgradePlan(typeId)}
          showCancelBtn={true}
          okText={t('billing.downgrade-confirm-ok')}
          description={
            <>
              {t('billing.downgrade-confirm-description')}
              <br />
              {t('billing.change-starts-next-cycle')}
            </>
          }
        />
      ),
    };

    dispatch(turnModalOn(modalData));
  };

  const editPlan = () => {
    const modalData: DrawerData = {
      type: DrawerType.EDIT_PLAN,
      title: t('organisation.edit-details'),
      component: (
        <EditPlan
          currentSubscriptionPlan={currentSubscription!}
          updatePlan={updatePlan}
          close={closeDrawer}
        />
      ),
    };

    dispatch(turnDrawerOn(modalData));
  };

  const redirectPage = (url: string) => {
    navigate(url);
  };

  const checkoutToUpgradePlan = (typeId: string) => {
    navigate(`/pricing/checkout/${typeId}?changePlan=true`);
  };

  const renderCancelPlanWindow = () => {
    const modalData: ModalData = {
      type: WindowType.WARNING,
      title: (
        <span style={{ fontSize: 16, fontWeight: 600 }}>
          {t('billing.cancel-plan-help-title')}
        </span>
      ),
      icon: <BreakingHeartIcon />,
      component: (
        <ConfirmBoxContainer
          callback={() => {
            dispatch(turnModalOn(modalData));
            renderContactUsWindow(false);
          }}
          callback2={() =>
            currentUser &&
            dispatch(cancelSubscriptionRequest(currentUser?.accountUuid))
          }
          showCancelBtn={false}
          okText={t('billing.chat-with-us')}
          okText2={t('billing.cancel-plan')}
          description={
            <>
              {t('billing.cancel-plan-modal-line-1')}
              <br />
              {t('billing.cancel-plan-modal-line-2')}
              <br />
              <b>{t('billing.please-note')}</b>,{' '}
              {t('billing.cancel-plan-modal-line-3')}
            </>
          }
        />
      ),
    };

    dispatch(turnModalOn(modalData));
  };

  const renderContactUsWindow = (isEnterprise: boolean) => {
    contactUsForm.setFieldValue('message', '');
    const modalData: ModalData = {
      type: WindowType.WARNING,
      onClose: () => contactUsForm.setFieldValue('message', ''),
      title: isEnterprise ? (
        <div style={{ display: 'block' }}>
          <CenterLogo
            width="200px"
            height="200px"
            src={EmailLogo}
            style={{ paddingBottom: 15 }}
          />
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {t('billing.reach-out-title')}
          </span>
          <br />
          <br />
          <span style={{ fontSize: 14, fontWeight: 400 }}>
            {t('billing.enterprise-contact-intro')}
            <br />
            <br /> {t('billing.leave-note-before-send')}
          </span>
        </div>
      ) : (
        <div style={{ display: 'block' }}>
          <CenterLogo
            width="200px"
            height="200px"
            src={EmailLogo}
            style={{ paddingBottom: 15 }}
          />
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {t('billing.reach-out-title')}
          </span>
          <br />
          <br />
          <span style={{ fontSize: 14, fontWeight: 400 }}>
            {t('billing.cancel-account-contact-intro')}
            <br />
            <br /> {t('billing.leave-note-before-send')}
          </span>
        </div>
      ),
      component: (
        <ConfirmBoxContainer
          callback={() => {
            dispatch(
              sendContactUsEmailRequest({
                message: contactUsForm.getFieldValue('message'),
                subject: isEnterprise
                  ? ContactUsMessageType.ENTERPRISE
                  : ContactUsMessageType.CANCELATION,
              })
            );
            contactUsForm.setFieldValue('message', '');
          }}
          showCancelBtn={false}
          okText={
            isEnterprise ? t('billing.request-more-info') : t('billing.request-call')
          }
          description={
            <Form form={contactUsForm}>
              <Space direction="vertical" style={{ width: '100%', rowGap: 30 }}>
                <Form.Item name="message">
                  <TextArea
                    rows={4}
                    style={{ width: '100%', marginTop: '20px' }}
                  />
                </Form.Item>
              </Space>
            </Form>
          }
        />
      ),
    };

    dispatch(turnModalOn(modalData));
  };

  return (
    <React.Fragment>
      {showPage && (
        <>
          <Helmet>
            <title>{`${t('navigation.billing')} | ${process.env.PROJECT_NAME}`}</title>
            <meta name="description" content={t('billing.meta-description-billing')} />
          </Helmet>

          <BillingComponent
            backToChangePlan={backToChangePlan}
            getFeaturesOfPlans={getFeaturesOfPlans}
            editPlan={editPlan}
            currentSubscription={currentSubscription!}
            redirectPage={redirectPage}
            renderCancelPlanWindow={renderCancelPlanWindow}
            invoices={invoices}
            updateCard={updateCard}
            isLoading={isLoading}
            subscriptionPlanTypes={subscriptionPlanTypes}
            checkoutToUpgradePlan={checkoutToUpgradePlan}
            renderContactUsWindow={renderContactUsWindow}
            downgradePlan={downgradePlan}
          />
        </>
      )}
    </React.Fragment>
  );
};
